import _ from "lodash";
import { ReactElement, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { CheckboxControlledMui } from "@ryde/controls";
import { useBulkEditFormContext, useCrudEditContext } from "@ryde/redux";
import {
  bulkSelectedControlsAtomFamily,
  conditionFieldNamesSF,
  controlNameMetadataResolve,
  fieldValuesProxySF,
  getBulkFormFieldName,
  ICustomField,
  IExtendableDto,
  isEntityWithAdminMode,
  profilePermissionsSelector,
  RydeUiUnit,
  systemSettingsDtoAtom,
  unique,
  useHasPermissionCustomField,
} from "@ryde/shared";

import { useRideHeaderPriceVisibility } from "../drawers/components/ride-edit-form/components/hooks/use-ride-header-price-visibility";
import { RenderTemplate } from "./_core";
import {
  BulkFieldsStackStyled,
  BulkGroupHeaderWrapperStyled,
  BulkRowWrapperStyled,
} from "./form.controls.bulk.styled";
import {
  doesControlRequireBulkAction,
  isBulkControlRowVisible,
  isBulkFormGroupHeaderControl,
} from "./form.controls.bulk.utils";

interface IEditFormWrapper {
  controls: ICustomField[];
  entity: RydeUiUnit;
}

export const FormControlsBulkWrapper = ({
  controls,
  entity,
}: IEditFormWrapper): ReactElement => {
  const { getValues, clearErrors } = useFormContext();
  const {
    entity: crudEntity,
    data: { dto },
  } = useCrudEditContext();
  const { entity: bulkFormEntity } = useBulkEditFormContext();
  const entityComputed = crudEntity || bulkFormEntity;
  const entityNormalized =
    entityComputed === "unknown" ? undefined : entityComputed;
  const { getFieldPermissions } = useHasPermissionCustomField();
  const roleDataItem = useRecoilValue(profilePermissionsSelector);
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const { shouldShowClientPrice, shouldShowSupplierPrice } =
    useRideHeaderPriceVisibility();
  const curValues = getValues();
  const isAdminModeEntity =
    !!entityNormalized && isEntityWithAdminMode(entityNormalized);

  const [selectedFields, setIsSelected] = useRecoilState(
    bulkSelectedControlsAtomFamily(entity),
  );
  const setFieldsSnapshot = useSetRecoilState(fieldValuesProxySF(entity));
  const conditionFieldNames = useRecoilValue(conditionFieldNamesSF(entity));

  // Only condition-driving values can change what this form renders or validates,
  // so watching the whole form would re-render every row on every keystroke.
  const conditionValues = useWatch({ name: conditionFieldNames, exact: true });

  useEffect(() => {
    // Deep clone: react-hook-form mutates its own values object in place.
    setFieldsSnapshot(_.cloneDeep(getValues()));
  }, [conditionValues, getValues, setFieldsSnapshot]);

  const selectedFieldsSet = useMemo(
    () => new Set(selectedFields),
    [selectedFields],
  );

  const handleSelectToggle = useEvent((control: ICustomField): void => {
    const controlName = controlNameMetadataResolve(control);
    const bulkActionFieldName = getBulkFormFieldName(controlName);
    const shouldToggleBulkActionField = doesControlRequireBulkAction({
      control,
      entity,
    });

    let checked: string[];

    if (!selectedFieldsSet.has(controlName)) {
      checked = unique([...selectedFields, controlName]);

      if (shouldToggleBulkActionField) {
        checked = unique([...checked, bulkActionFieldName]);
      }
    } else {
      checked = selectedFields.filter(name => name !== controlName);

      if (shouldToggleBulkActionField) {
        checked = checked.filter(name => name !== bulkActionFieldName);
      }

      clearErrors(controlName);

      if (shouldToggleBulkActionField) {
        clearErrors(bulkActionFieldName);
      }
    }

    setIsSelected(checked);
  });

  return (
    <BulkFieldsStackStyled>
      {controls.map((control, index) => {
        const { isFieldVisible, isFieldDisabled } = getFieldPermissions({
          readPermission: control.readPermission,
          updatePermission: control.updatePermission,
          formEntity: entityNormalized,
        });

        const isRowVisible = isBulkControlRowVisible({
          control,
          formValues: curValues,
          dto: dto as IExtendableDto,
          permissions: roleDataItem?.permissions,
          systemParams: systemSettingsDto?.metadata ?? {},
          isFieldVisible,
          isAdminModeEntity,
          shouldShowClientPrice,
          shouldShowSupplierPrice,
          // Bulk update always targets existing records (context isEdit may be undefined on grid)
          isEdit: true,
        });

        if (!isRowVisible) {
          return null;
        }

        const rowKey = control.id + "_" + index;

        if (isBulkFormGroupHeaderControl(control)) {
          return (
            <BulkGroupHeaderWrapperStyled key={rowKey}>
              <RenderTemplate formVariant="bulk" {...control} />
            </BulkGroupHeaderWrapperStyled>
          );
        }

        return (
          <BulkRowWrapperStyled key={rowKey}>
            <div className="checkbox-container">
              {!isFieldDisabled && (
                <CheckboxControlledMui
                  width={14}
                  height={14}
                  checked={selectedFieldsSet.has(
                    controlNameMetadataResolve(control),
                  )}
                  setCheckedToggle={() => handleSelectToggle(control)}
                  e2eData="bulk-update-selected-checkbox"
                />
              )}
            </div>
            <RenderTemplate formVariant="bulk" {...control} />
          </BulkRowWrapperStyled>
        );
      })}
    </BulkFieldsStackStyled>
  );
};
